import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Calendar,
  Link2,
  Globe,
  Shield,
  CheckCircle,
  LogIn,
  LogOut,
  AlertCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Video,
  User,
  Clock,
  XCircle,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'

// ── Calendar constants ────────────────────────────────────────────────────────
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOUR_HEIGHT = 64           // px per 1 hour
const DAY_START_HOUR = 7         // 7:00 AM
const DAY_END_HOUR = 22          // 10:00 PM
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR  // 15
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT       // 960px

// Build hour labels: 7 AM … 9 PM
const HOUR_LABELS = Array.from({ length: TOTAL_HOURS }, (_, i) => {
  const h = DAY_START_HOUR + i
  return h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h - 12} PM`
})

interface BookingItem {
  booking_id: number
  session_id: number | null
  status: string
  scheduled_start_utc: string
  scheduled_end_utc: string
  duration_minutes: number
  client_name: string | null
  client_email: string
  zoom_join_url: string | null
  zoom_host_url: string | null
  zoom_password: string | null
}

interface Toast {
  type: 'success' | 'error'
  text: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getWeekDates(offset: number): Date[] {
  const now = new Date()
  const dayOfWeek = now.getDay() // 0 = Sun
  const monday = new Date(now)
  monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7) + offset * 7)
  monday.setHours(0, 0, 0, 0)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return d
  })
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

function bookingTopPx(startUtc: string): number {
  const d = new Date(startUtc)
  const hours = d.getHours() + d.getMinutes() / 60
  return Math.max(0, (hours - DAY_START_HOUR) * HOUR_HEIGHT)
}

function bookingHeightPx(durationMinutes: number): number {
  return Math.max(24, (durationMinutes / 60) * HOUR_HEIGHT)
}

function formatLastSynced(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000)
  if (secs < 10) return 'just now'
  if (secs < 60) return `${secs}s ago`
  const mins = Math.floor(secs / 60)
  return `${mins}m ago`
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const STATUS_STYLE: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  confirmed:       { bg: 'rgba(37,99,235,0.10)', border: '#2563eb', text: '#1d4ed8', dot: '#2563eb' },
  pending_payment: { bg: 'rgba(217,119,6,0.10)',  border: '#d97706', text: '#92400e', dot: '#d97706' },
  cancelled:       { bg: 'rgba(107,114,128,0.08)', border: '#9ca3af', text: '#6b7280', dot: '#9ca3af' },
  completed:       { bg: 'rgba(22,163,74,0.10)',  border: '#16a34a', text: '#15803d', dot: '#16a34a' },
}

export default function InterviewerSchedule() {
  const navigate = useNavigate()
  void navigate // kept for future sub-route navigation
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('schedule')

  // ── Weekly calendar state ──────────────────────────────────────────────────
  const [bookings, setBookings] = useState<BookingItem[]>([])
  const [weekOffset, setWeekOffset] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const weekDates = getWeekDates(weekOffset)

  // ── Cancel modal state ─────────────────────────────────────────────────────
  const [selectedBooking, setSelectedBooking] = useState<BookingItem | null>(null)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  // ── Calendly state ─────────────────────────────────────────────────────────
  const [calendlyConnected, setCalendlyConnected] = useState(false)
  const [calendlyConnectedAt, setCalendlyConnectedAt] = useState('')
  const [calendlyEventTypeName, setCalendlyEventTypeName] = useState<string | null>(null)
  const [calendlyEventTypeDuration, setCalendlyEventTypeDuration] = useState<number | null>(null)
  const [calendlyLoading, setCalendlyLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)

  // Toast notification
  const [toast, setToast] = useState<Toast | null>(null)

  // Availability toggle
  const [isAvailable, setIsAvailable] = useState(true)
  const [togglingAvailability, setTogglingAvailability] = useState(false)

  // Preferences
  const [prefTimezone, setPrefTimezone] = useState('')
  const [prefLanguage, setPrefLanguage] = useState('')
  const [savedTimezone, setSavedTimezone] = useState('')
  const [savedLanguage, setSavedLanguage] = useState('')
  const [profileSnapshot, setProfileSnapshot] = useState<Record<string, unknown>>({})
  const [savingPreferences, setSavingPreferences] = useState(false)

  const preferencesChanged = prefTimezone !== savedTimezone || prefLanguage !== savedLanguage

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(t)
  }, [toast])

  // ── Read ?calendly= query param after OAuth redirect ───────────────────────
  useEffect(() => {
    const status = searchParams.get('calendly')
    if (!status) return
    if (status === 'connected') {
      setToast({ type: 'success', text: 'Calendly connected successfully!' })
      setActiveTab('calendly')
      loadCalendlyStatus()
    } else if (status === 'error') {
      setToast({ type: 'error', text: 'Could not connect Calendly. Please try again.' })
      setActiveTab('calendly')
    }
    setSearchParams({}, { replace: true })
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadBookings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    try {
      const res = await interviewerApi.getMySchedule()
      const items: BookingItem[] = (res.bookings ?? res ?? []).filter(
        (b: BookingItem) => !['payment_expired', 'payment_failed'].includes(b.status)
      )
      setBookings(items)
      setLastSynced(new Date())
    } catch (err) {
      console.error('Failed to load schedule:', err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  const loadCalendlyStatus = async () => {
    setCalendlyLoading(true)
    try {
      const status = await interviewerApi.getCalendlyStatus()
      setCalendlyConnected(status.connected)
      setCalendlyConnectedAt(status.connected_at ?? '')
      setCalendlyEventTypeName(status.event_type_name ?? null)
      setCalendlyEventTypeDuration(status.event_type_duration ?? null)
    } catch {
      setCalendlyConnected(false)
      setCalendlyEventTypeName(null)
      setCalendlyEventTypeDuration(null)
    } finally {
      setCalendlyLoading(false)
    }
  }

  const loadAvailability = async () => {
    try {
      const res = await interviewerApi.getSchedule().catch(() => null)
      if (res) {
        const profile = res.profile ?? res
        setIsAvailable(profile.is_available !== false)
      }
    } catch { /* ignore */ }
  }

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadBookings()
    loadCalendlyStatus()
    loadAvailability()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-poll every 30 s while schedule tab is active ─────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'schedule' && document.visibilityState === 'visible') {
        loadBookings(true)
      }
    }, 30_000)
    return () => clearInterval(interval)
  }, [activeTab, loadBookings])

  // ── Refetch when user returns to this browser tab ────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && activeTab === 'schedule') {
        loadBookings(true)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [activeTab, loadBookings])

  // ── Cancel booking ────────────────────────────────────────────────────────
  const handleCancelBooking = async () => {
    if (!selectedBooking) return
    setCancelling(true)
    try {
      await interviewerApi.cancelBooking(selectedBooking.booking_id, cancelReason || undefined)
      setToast({ type: 'success', text: 'Booking cancelled successfully.' })
      setSelectedBooking(null)
      setShowCancelConfirm(false)
      setCancelReason('')
      loadBookings(true)
    } catch (err) {
      setToast({ type: 'error', text: err instanceof Error ? err.message : 'Failed to cancel booking.' })
    } finally {
      setCancelling(false)
    }
  }

  const handleConfirmConnect = async () => {
    setConnecting(true)
    try {
      const { authorization_url } = await interviewerApi.getCalendlyAuthUrl()
      window.location.href = authorization_url
    } catch {
      setShowConnectModal(false)
      setConnecting(false)
      setToast({ type: 'error', text: 'Could not initiate Calendly connection. Try again.' })
    }
  }

  const handleDisconnectCalendly = async () => {
    setDisconnecting(true)
    setShowDisconnectModal(false)
    try {
      await interviewerApi.disconnectCalendly()
      setCalendlyConnected(false)
      setCalendlyConnectedAt('')
      setCalendlyEventTypeName(null)
      setCalendlyEventTypeDuration(null)
      setToast({ type: 'success', text: 'Calendly disconnected. You can reconnect at any time.' })
    } catch {
      setToast({ type: 'error', text: 'Failed to disconnect. Please try again.' })
    } finally {
      setDisconnecting(false)
    }
  }

  const handleToggleAvailability = async () => {
    setTogglingAvailability(true)
    try {
      await interviewerApi.toggleAvailability(!isAvailable)
      setIsAvailable(!isAvailable)
    } catch (err) {
      console.error('Failed to toggle availability:', err)
    } finally {
      setTogglingAvailability(false)
    }
  }

  const loadPreferences = async () => {
    try {
      const res = await interviewerApi.getProfile()
      const profile = res.data ?? res
      setPrefTimezone(profile.timezone ?? '')
      setPrefLanguage(profile.language ?? '')
      setSavedTimezone(profile.timezone ?? '')
      setSavedLanguage(profile.language ?? '')
      setProfileSnapshot({
        bio: profile.bio,
        specializations: profile.specializations,
        years_experience: profile.years_experience,
        avatar_url: profile.avatar_url,
      })
    } catch (err) {
      console.error('Failed to load preferences:', err)
    }
  }

  const handleSavePreferences = async () => {
    setSavingPreferences(true)
    try {
      await interviewerApi.updateProfile({
        ...profileSnapshot,
        timezone: prefTimezone,
        language: prefLanguage,
      })
      setSavedTimezone(prefTimezone)
      setSavedLanguage(prefLanguage)
      setToast({ type: 'success', text: 'Preferences saved.' })
    } catch {
      setToast({ type: 'error', text: 'Failed to save preferences. Please try again.' })
    } finally {
      setSavingPreferences(false)
    }
  }

  // ── Bookings for a given day ───────────────────────────────────────────────
  const bookingsForDay = (date: Date) =>
    bookings.filter((b) => isSameDay(new Date(b.scheduled_start_utc), date))

  if (loading) {
    return <div className="loading-screen">Loading schedule...</div>
  }

  return (
    <div>
      {/* ── Toast notification ── */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 20px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            background: toast.type === 'error' ? '#fff5f5' : '#f0fff4',
            color: toast.type === 'error' ? 'var(--danger)' : 'var(--success)',
            border: `1px solid ${toast.type === 'error' ? 'rgba(229,62,62,0.25)' : 'rgba(72,187,120,0.25)'}`,
            maxWidth: 360,
            animation: 'slideIn 0.2s ease',
          }}
        >
          <style>{`
            @keyframes slideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
          {toast.type === 'error'
            ? <AlertCircle size={18} />
            : <CheckCircle size={18} />}
          {toast.text}
        </div>
      )}

      <div className="page-header">
        <h1 className="page-title">My Schedule</h1>
        <p className="page-subtitle">
          View your interview schedule and manage Calendly availability
        </p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedule')}
        >
          <Calendar size={16} />
          Weekly Schedule
        </button>
        <button
          className={`tab ${activeTab === 'calendly' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendly')}
        >
          <Link2 size={16} />
          Calendly Integration
          {/* Green dot when connected */}
          {calendlyConnected && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--success)',
                display: 'inline-block',
                marginLeft: 5,
                flexShrink: 0,
              }}
            />
          )}
        </button>
        <button
          className={`tab ${activeTab === 'availability' ? 'active' : ''}`}
          onClick={() => setActiveTab('availability')}
        >
          <Globe size={16} />
          Availability
        </button>
        <button
          className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => { setActiveTab('preferences'); loadPreferences() }}
        >
          <Settings size={16} />
          Preferences
        </button>
      </div>

      {/* ── Weekly Schedule tab ── */}
      {activeTab === 'schedule' && (
        <>
          {/* toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {/* week navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button className="is-nav-btn" onClick={() => setWeekOffset(w => w - 1)}>
                <ChevronLeft size={16} />
              </button>
              <button
                className="is-nav-btn is-nav-today"
                onClick={() => setWeekOffset(0)}
                disabled={weekOffset === 0}
              >
                Today
              </button>
              <button className="is-nav-btn" onClick={() => setWeekOffset(w => w + 1)}>
                <ChevronRight size={16} />
              </button>
            </div>

            <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-dark)' }}>
              {fmtDate(weekDates[0])} – {fmtDate(weekDates[6])}
            </span>

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* live sync indicator */}
              {lastSynced && !refreshing && (
                <span className="is-sync-label">
                  <span className="is-sync-dot" />
                  Synced {formatLastSynced(lastSynced)}
                </span>
              )}
              <button
                className="is-nav-btn"
                onClick={() => loadBookings(true)}
                disabled={refreshing}
                title="Refresh"
              >
                <RefreshCw size={15} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
                {refreshing ? 'Syncing…' : 'Sync'}
              </button>
            </div>
          </div>

          {/* policy notice */}
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '12px 18px', background: 'rgba(45,55,72,0.03)' }}>
            <Shield size={16} style={{ color: 'var(--primary-navy)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Bookings sync automatically from Calendly. Click any booking to view details or cancel.
            </span>
          </div>

          {/* calendar grid */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 720 }}>

                {/* header row: time col + 7 day cols */}
                <div className="is-cal-header-row">
                  <div className="is-time-col" />
                  {weekDates.map((date, i) => {
                    const isToday = isSameDay(date, new Date())
                    const dayBookings = bookingsForDay(date).filter(b => b.status !== 'cancelled')
                    return (
                      <div key={i} className={`is-day-header ${isToday ? 'is-today' : ''}`}>
                        <span className="is-day-label">{DAY_LABELS[i]}</span>
                        <span className={`is-day-date ${isToday ? 'is-today-date' : ''}`}>
                          {date.getDate()}
                        </span>
                        {dayBookings.length > 0 && (
                          <span className="is-day-count">{dayBookings.length}</span>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* body: time labels + day columns */}
                <div style={{ display: 'flex' }}>
                  {/* time labels */}
                  <div className="is-time-col" style={{ position: 'relative', height: GRID_HEIGHT }}>
                    {HOUR_LABELS.map((label, i) => (
                      <div
                        key={label}
                        style={{
                          position: 'absolute',
                          top: i * HOUR_HEIGHT - 8,
                          right: 8,
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* day columns */}
                  {weekDates.map((date, colIdx) => {
                    const isToday = isSameDay(date, new Date())
                    const dayBkgs = bookingsForDay(date)
                    return (
                      <div
                        key={colIdx}
                        className={`is-day-col ${isToday ? 'is-today-col' : ''}`}
                        style={{ height: GRID_HEIGHT }}
                      >
                        {/* hour grid lines */}
                        {HOUR_LABELS.map((_, i) => (
                          <div
                            key={i}
                            style={{
                              position: 'absolute',
                              top: i * HOUR_HEIGHT,
                              left: 0, right: 0,
                              borderTop: `1px solid ${i === 0 ? 'transparent' : 'var(--border)'}`,
                            }}
                          />
                        ))}

                        {/* booking cards */}
                        {dayBkgs.map((bk) => {
                          const top = bookingTopPx(bk.scheduled_start_utc)
                          const height = bookingHeightPx(bk.duration_minutes)
                          const style = STATUS_STYLE[bk.status] ?? STATUS_STYLE.confirmed
                          const isCancelled = bk.status === 'cancelled'
                          return (
                            <div
                              key={bk.booking_id}
                              className="is-booking-card"
                              style={{
                                top,
                                height,
                                background: style.bg,
                                borderLeft: `3px solid ${style.border}`,
                                opacity: isCancelled ? 0.55 : 1,
                                cursor: isCancelled ? 'default' : 'pointer',
                              }}
                              onClick={() => !isCancelled && setSelectedBooking(bk)}
                            >
                              <div className="is-bk-name">
                                {bk.client_name || bk.client_email.split('@')[0]}
                              </div>
                              <div className="is-bk-time" style={{ color: style.text }}>
                                {fmtTime(bk.scheduled_start_utc)} · {bk.duration_minutes}m
                              </div>
                              {isCancelled && (
                                <div className="is-bk-cancelled">Cancelled</div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* empty state */}
            {bookings.filter(b => {
              const d = new Date(b.scheduled_start_utc)
              return weekDates.some(wd => isSameDay(d, wd))
            }).length === 0 && (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                No bookings this week. Clients will appear here as they book sessions.
              </div>
            )}
          </div>

          {/* ── Booking detail modal ── */}
          {selectedBooking && (
            <div className="is-modal-overlay" onClick={() => { setSelectedBooking(null); setShowCancelConfirm(false); setCancelReason('') }}>
              <div className="is-modal" onClick={(e) => e.stopPropagation()}>
                {/* header */}
                <div className="is-modal-header">
                  <div>
                    <h3 className="is-modal-title">Interview Session</h3>
                    <span
                      className="is-modal-status"
                      style={{
                        background: (STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).bg,
                        color: (STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).text,
                        border: `1px solid ${(STATUS_STYLE[selectedBooking.status] ?? STATUS_STYLE.confirmed).border}`,
                      }}
                    >
                      {selectedBooking.status.replace('_', ' ')}
                    </span>
                  </div>
                  <button className="is-modal-close" onClick={() => { setSelectedBooking(null); setShowCancelConfirm(false); setCancelReason('') }}>
                    <X size={18} />
                  </button>
                </div>

                {/* detail rows */}
                <div className="is-modal-body">
                  <div className="is-modal-row">
                    <User size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div>
                      <div className="is-modal-label">Client</div>
                      <div className="is-modal-val">
                        {selectedBooking.client_name || '—'}
                        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>
                          {selectedBooking.client_email}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="is-modal-row">
                    <Clock size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div>
                      <div className="is-modal-label">Date & Time</div>
                      <div className="is-modal-val">
                        {new Date(selectedBooking.scheduled_start_utc).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        {' · '}{fmtTime(selectedBooking.scheduled_start_utc)}
                        {' – '}{fmtTime(selectedBooking.scheduled_end_utc)}
                        {' ('}{selectedBooking.duration_minutes} min{')'}
                      </div>
                    </div>
                  </div>

                  {selectedBooking.zoom_host_url && (
                    <div className="is-modal-row">
                      <Video size={15} style={{ color: '#2563eb', flexShrink: 0 }} />
                      <div>
                        <div className="is-modal-label">Zoom (Host Link)</div>
                        <a
                          href={selectedBooking.zoom_host_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="is-modal-zoom-link"
                        >
                          Start Meeting as Host ↗
                        </a>
                        {selectedBooking.zoom_password && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                            Password: <strong style={{ color: 'var(--text-dark)' }}>{selectedBooking.zoom_password}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* cancel section */}
                {selectedBooking.status === 'confirmed' && (
                  <div className="is-modal-footer">
                    {!showCancelConfirm ? (
                      <button
                        className="is-cancel-trigger-btn"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        <XCircle size={15} />
                        Cancel this booking
                      </button>
                    ) : (
                      <div className="is-cancel-confirm">
                        <p className="is-cancel-warn">
                          This will cancel the booking and notify the client by email.
                        </p>
                        <textarea
                          className="form-input"
                          placeholder="Reason for cancellation (optional)…"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          rows={2}
                          style={{ resize: 'vertical', fontSize: 13 }}
                        />
                        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                          <button
                            className="btn"
                            style={{ background: '#dc2626', color: '#fff', border: 'none' }}
                            onClick={handleCancelBooking}
                            disabled={cancelling}
                          >
                            {cancelling ? 'Cancelling…' : 'Confirm Cancel'}
                          </button>
                          <button
                            className="btn btn-outline"
                            onClick={() => { setShowCancelConfirm(false); setCancelReason('') }}
                          >
                            Keep Booking
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

            .is-sync-label {
              display: inline-flex; align-items: center; gap: 5px;
              font-size: 12px; color: var(--text-muted); font-weight: 500;
            }
            .is-sync-dot {
              width: 7px; height: 7px; border-radius: 50%; background: #16a34a;
              animation: pulse 2.4s ease-in-out infinite;
            }

            .is-nav-btn {
              display: inline-flex; align-items: center; gap: 5px;
              padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 500;
              background: white; border: 1px solid var(--border); color: var(--text-dark);
              cursor: pointer; transition: all 0.15s;
            }
            .is-nav-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
            .is-nav-btn:disabled { opacity: 0.45; cursor: default; }
            .is-nav-today { min-width: 64px; justify-content: center; }

            .is-cal-header-row {
              display: grid;
              grid-template-columns: 52px repeat(7, 1fr);
              border-bottom: 1px solid var(--border);
              background: #fafbfc;
            }
            .is-time-col { width: 52px; flex-shrink: 0; }
            .is-day-header {
              display: flex; flex-direction: column; align-items: center;
              padding: 10px 4px; gap: 4px; border-left: 1px solid var(--border);
            }
            .is-day-header.is-today { background: rgba(37,99,235,0.04); }
            .is-day-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
            .is-day-date {
              width: 28px; height: 28px; border-radius: 50%;
              display: flex; align-items: center; justify-content: center;
              font-size: 14px; font-weight: 600; color: var(--text-dark);
            }
            .is-today-date { background: var(--primary-navy, #1e2432); color: white !important; }
            .is-day-count {
              font-size: 10px; font-weight: 700; background: #2563eb; color: white;
              border-radius: 10px; padding: 1px 6px; min-width: 16px; text-align: center;
            }

            .is-day-col {
              position: relative; flex: 1;
              border-left: 1px solid var(--border);
              overflow: visible;
            }
            .is-today-col { background: rgba(37,99,235,0.02); }

            .is-booking-card {
              position: absolute; left: 3px; right: 3px;
              border-radius: 6px; padding: 5px 7px;
              overflow: hidden; transition: filter 0.15s;
              z-index: 1;
            }
            .is-booking-card:hover:not([style*="cursor: default"]) { filter: brightness(0.96); }
            .is-bk-name { font-size: 11.5px; font-weight: 700; color: var(--text-dark); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .is-bk-time { font-size: 10.5px; font-weight: 500; }
            .is-bk-cancelled { font-size: 10px; font-weight: 600; color: #9ca3af; margin-top: 2px; }

            /* modal */
            .is-modal-overlay {
              position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
              display: flex; align-items: center; justify-content: center; padding: 24px;
            }
            .is-modal {
              background: white; border-radius: 16px; width: 100%; max-width: 460px;
              box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
            }
            .is-modal-header {
              display: flex; align-items: flex-start; justify-content: space-between;
              padding: 20px 22px 16px; border-bottom: 1px solid var(--border);
            }
            .is-modal-title { font-size: 17px; font-weight: 700; color: var(--text-dark); margin: 0 0 6px; }
            .is-modal-status {
              display: inline-block; padding: 3px 10px; border-radius: 20px;
              font-size: 11.5px; font-weight: 600; text-transform: capitalize;
            }
            .is-modal-close {
              background: none; border: none; cursor: pointer; padding: 4px;
              color: var(--text-muted); border-radius: 6px; display: flex;
            }
            .is-modal-close:hover { background: #f3f4f6; }
            .is-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 16px; }
            .is-modal-row { display: flex; align-items: flex-start; gap: 12px; }
            .is-modal-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 3px; }
            .is-modal-val { font-size: 13.5px; font-weight: 500; color: var(--text-dark); }
            .is-modal-zoom-link { font-size: 13.5px; font-weight: 600; color: #2563eb; text-decoration: none; }
            .is-modal-zoom-link:hover { text-decoration: underline; }
            .is-modal-footer { padding: 16px 22px; border-top: 1px solid var(--border); background: #fafbfc; }
            .is-cancel-trigger-btn {
              display: inline-flex; align-items: center; gap: 7px;
              background: none; border: 1px solid rgba(220,38,38,0.3);
              color: #dc2626; border-radius: 8px; padding: 8px 14px;
              font-size: 13px; font-weight: 600; cursor: pointer;
              transition: all 0.15s;
            }
            .is-cancel-trigger-btn:hover { background: rgba(220,38,38,0.06); }
            .is-cancel-warn { font-size: 13px; color: var(--text-muted); margin: 0 0 10px; }
            .is-cancel-confirm { display: flex; flex-direction: column; gap: 6px; }
          `}</style>
        </>
      )}

      {/* ── Calendly Integration tab ── */}
      {activeTab === 'calendly' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Calendly Integration
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Connect your Calendly account so clients can see your real availability and book sessions.
          </p>

          {calendlyLoading ? (
            /* ── Loading state ── */
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-muted)', fontSize: 14 }}>
              <div style={{
                width: 20, height: 20,
                border: '2px solid var(--border)',
                borderTopColor: 'var(--primary-navy)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                flexShrink: 0,
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              Checking Calendly status…
            </div>
          ) : calendlyConnected ? (
            /* ── Connected state ── */
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: 20,
                background: 'rgba(72, 187, 120, 0.06)',
                borderRadius: 12,
                border: '1px solid rgba(72, 187, 120, 0.22)',
                marginBottom: 20,
              }}>
                <div style={{
                  width: 44, height: 44,
                  borderRadius: '50%',
                  background: 'rgba(72, 187, 120, 0.14)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <CheckCircle size={22} style={{ color: 'var(--success)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
                    Calendly Account Connected
                  </div>
                  {calendlyConnectedAt && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      Connected on {new Date(calendlyConnectedAt).toLocaleDateString()}
                    </div>
                  )}
                  {calendlyEventTypeName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20,
                        background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)',
                        fontSize: 12, fontWeight: 600, color: '#1d4ed8',
                      }}>
                        {calendlyEventTypeName}
                        {calendlyEventTypeDuration ? ` · ${calendlyEventTypeDuration} min` : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>


              <button
                className="btn btn-outline"
                onClick={() => setShowDisconnectModal(true)}
                disabled={disconnecting}
                style={{ color: 'var(--danger)', borderColor: 'rgba(229,62,62,0.4)' }}
              >
                <LogOut size={16} />
                {disconnecting ? 'Disconnecting…' : 'Disconnect Calendly'}
              </button>
            </div>
          ) : (
            /* ── Not connected state ── */
            <div>
              <div style={{
                padding: 18,
                background: 'rgba(45, 55, 72, 0.03)',
                borderRadius: 10,
                border: '1px solid var(--border)',
                marginBottom: 24,
              }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
                  What happens when you connect?
                </div>
                <ul style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 18, margin: 0, lineHeight: 2 }}>
                  <li>Clients see your 60-minute available slots (9 AM – 5 PM) for any date they pick</li>
                  <li>Slots stay in sync with your Calendly calendar automatically</li>
                  <li>No manual availability entry needed</li>
                </ul>
              </div>

              <button className="btn btn-primary" onClick={() => setShowConnectModal(true)}>
                <LogIn size={18} />
                Connect with Calendly
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Connect Permissions Modal ── */}
      {showConnectModal && (
        <div className="is-modal-overlay" onClick={() => !connecting && setShowConnectModal(false)}>
          <div className="is-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="is-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(0,105,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="3" stroke="#0069ff" strokeWidth="2"/>
                    <path d="M16 2v4M8 2v4M3 10h18" stroke="#0069ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="is-modal-title" style={{ marginBottom: 2 }}>Connect Calendly</h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                    Interview Dress Rehearsal will request the following access
                  </p>
                </div>
              </div>
              <button className="is-modal-close" onClick={() => !connecting && setShowConnectModal(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Permissions list */}
            <div className="is-modal-body" style={{ gap: 0, padding: '16px 22px' }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                To generate your 60-minute interview slots, we need read access to the following Calendly data:
              </p>

              {[
                {
                  icon: '🗓️',
                  title: 'Availability Schedules',
                  desc: 'Your working hours (e.g. 9 AM – 5 PM Mon–Fri) to calculate open interview slots',
                },
                {
                  icon: '⏳',
                  title: 'Busy / Blocked Times',
                  desc: 'Existing calendar events so we never offer a slot that is already taken',
                },
                {
                  icon: '🔗',
                  title: 'Event Types',
                  desc: 'Your scheduling page URLs and event durations for proper slot configuration',
                },
                {
                  icon: '👤',
                  title: 'Profile & Timezone',
                  desc: 'Your name and timezone so slots are shown in the correct local time to clients',
                },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '12px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                  <CheckCircle size={15} style={{ color: '#16a34a', flexShrink: 0, marginTop: 3, marginLeft: 'auto' }} />
                </div>
              ))}

              <div style={{
                marginTop: 16, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)',
                fontSize: 12, color: '#1d4ed8', lineHeight: 1.5,
              }}>
                <strong>Read-only access.</strong> We never write to your calendar or create/delete events on Calendly.
                You can disconnect at any time.
              </div>
            </div>

            {/* Footer */}
            <div className="is-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => setShowConnectModal(false)}
                disabled={connecting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmConnect}
                disabled={connecting}
              >
                {connecting ? (
                  <>
                    <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    Redirecting…
                  </>
                ) : (
                  <>
                    <LogIn size={15} />
                    Allow &amp; Connect
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Disconnect Confirmation Modal ── */}
      {showDisconnectModal && (
        <div className="is-modal-overlay" onClick={() => setShowDisconnectModal(false)}>
          <div className="is-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="is-modal-header">
              <h3 className="is-modal-title">Disconnect Calendly?</h3>
              <button className="is-modal-close" onClick={() => setShowDisconnectModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="is-modal-body" style={{ padding: '18px 22px' }}>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
                Disconnecting will stop syncing your availability. Clients will not see any available
                slots until you reconnect. Existing confirmed bookings are not affected.
              </p>
            </div>
            <div className="is-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowDisconnectModal(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: '#dc2626', color: '#fff', border: 'none' }}
                onClick={handleDisconnectCalendly}
              >
                <LogOut size={15} />
                Yes, Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Availability tab ── */}
      {activeTab === 'availability' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Availability Toggle
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Toggle your availability to accept new session bookings.
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 20,
              background: isAvailable ? 'rgba(72, 187, 120, 0.06)' : 'rgba(229, 62, 62, 0.06)',
              borderRadius: 12,
              border: `1px solid ${isAvailable ? 'rgba(72, 187, 120, 0.2)' : 'rgba(229, 62, 62, 0.2)'}`,
            }}
          >
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                {isAvailable ? 'Available for Booking' : 'Not Available'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                {isAvailable
                  ? 'Clients can book new sessions with you.'
                  : 'You are currently not accepting new bookings.'}
              </div>
            </div>
            <button
              className={isAvailable ? 'btn btn-danger' : 'btn btn-success'}
              style={{ padding: '10px 20px' }}
              onClick={handleToggleAvailability}
              disabled={togglingAvailability}
            >
              {togglingAvailability
                ? 'Updating...'
                : isAvailable
                  ? 'Go Unavailable'
                  : 'Go Available'}
            </button>
          </div>
        </div>
      )}
      {/* ── Preferences tab ── */}
      {activeTab === 'preferences' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Timezone and Language
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
            Set your timezone and preferred interview language.
          </p>

          <div className="form-group">
            <label className="form-label">Timezone</label>
            <select
              value={prefTimezone}
              onChange={(e) => setPrefTimezone(e.target.value)}
              className="form-input"
            >
              <option value="">Select your timezone</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">Greenwich Mean Time (GMT)</option>
              <option value="Europe/Paris">Central European Time (CET)</option>
              <option value="Asia/Kolkata">India Standard Time (IST)</option>
              <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              value={prefLanguage}
              onChange={(e) => setPrefLanguage(e.target.value)}
              className="form-input"
            >
              <option value="">Select interview language</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese (Mandarin)</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
              <option value="pt">Portuguese</option>
              <option value="hi">Hindi</option>
              <option value="ar">Arabic</option>
            </select>
          </div>

          <button
            className="btn btn-primary"
            disabled={savingPreferences || !preferencesChanged}
            style={{ marginTop: 8 }}
            onClick={handleSavePreferences}
          >
            {savingPreferences ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      )}
    </div>
  )
}
